// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
 import { minicartStyles } from "@gcg/App/Components/XngMiniCart/XngMiniCartStyles";
 import LazyImage, { LazyImageProps, LazyImageStyle } from "@gcg/MobiusOverrides/LazyImage/LazyImage";
 import ProductDescription, { ProductDescriptionStyles } from "@gcg/Overrides/Components/ProductDescription";
 import ProductPartNumbers, { ProductPartNumbersStyles } from "@gcg/Overrides/Components/ProductPartNumbers";
 import { screen } from "@gcg/XDL/Helpers";
 import { printStyles } from "@gcg/XDL/Utilities";
 import mergeToNew from "@insite/client-framework/Common/mergeToNew";
 import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
 import translate from "@insite/client-framework/Translate";
 import { OrderLineModel } from "@insite/client-framework/Types/ApiModels";
 import Clickable from "@insite/mobius/Clickable";
 import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
 import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
 import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
 import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
 import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
 import getColor from "@insite/mobius/utilities/getColor";
 import InjectableCss from "@insite/mobius/utilities/InjectableCss";
 import * as React from "react";
 import { FC } from "react";
 import { css } from "styled-components";

 export interface CustomOrderLineModel extends OrderLineModel {
     imagePath: string;
     qty: string;
     itemNumber: string;
     description: string;
     extendedUnitNetPrice: number;
     extendedUnitNetPriceDisplay: string;
     maxQty: string;
     notes: string;
     productName: string;
     productUrl: string;
     requiresQuote: boolean;
     unitNetPrice: number;
     unitNetPriceDisplay: string;
     uom: string;
     specNumber: string;
     manufacturerItem: string;
 }

 interface OwnProps {
     orderLine: CustomOrderLineModel;
     headerColumns: string[];
 }

 export interface OrderDetailsOrderLineProps {
     tableRowStyle: DataTableRowProps;
     tableCellGridContainerStyle: GridContainerProps;
     productDetailsGridStyle: GridItemProps;
     customWidthGridStyle: GridItemProps;
     fullWidthGridStyle: GridItemProps;
     halfWidthGridStyle: GridItemProps;
     productImageGridStyle: GridItemProps;
     productImageStyle: LazyImageProps;
     productDescriptionStyles?: ProductDescriptionStyles;
     wrapper?: InjectableCss;
     titleText?: TypographyPresentationProps;
     contentText?: TypographyPresentationProps;
     tableCellStyle: DataTableCellProps;
     tableCellGridStyle: GridItemProps;
     productPartNumbers?: ProductPartNumbersStyles;
 }

 export const orderDetailsOrderLineStyles: OrderDetailsOrderLineProps = {
     tableRowStyle: {
         evenRowCss: css`
             background: transparent;
         `,
         css: css`
             border-bottom: 1px solid ${getColor("common.ui4")};
             td {
                 padding: 16px 12px 0;

                 @media print {
                     padding-block: 20px !important;
                     max-width: calc(45% / 3);
                     flex-basis: calc(45% / 3);
                     &:first-child {
                         max-width: 55%;
                         flex-basis: 55%;
                     }
                 }
             }
             &&&:hover,
             &&&:focus {
                 background: transparent;
                 cursor: unset;
             }
             .hide-print {
                 ${printStyles.hideOnPrint}
             }
             ${screen("lte", "md")`
                  display: flex;
                  flex-direction: column;
                  td {
                      height: 100%;
                      padding: 6px 12px;
  
                      &:first-child {
                          padding-top: 20px;
                          margin-bottom: 20px;
                      }
                      &:last-child {
                          padding-block: 12px;
                      }
                  }
               `}
         `,
     },
     tableCellGridContainerStyle: {
         css: css`
             height: 100%;
             padding-bottom: 20px;

             @media print {
                 max-width: 100%;
                 flex-basis: 100%;

                 .image-print {
                     max-width: 150px;
                     margin-right: 20px;

                     ${LazyImageStyle} {
                         width: 150px;
                         height: 150px;
                     }
                 }
                 .product-details-print {
                     max-width: calc(100% - 170px);
                 }
             }
         `,
     },
     productDetailsGridStyle: {
         width: [12, 12, 12, 9, 9],
         css: css`
             ${screen("gt", "md")`
                  padding-left: 8px;
              `}
         `,
     },
     productDescriptionStyles: {
         productDetailLink: {
             color: "text.main",
             typographyProps: {
                 weight: 500,
             },
         },
         productDetailDescription: {
             weight: 500,
         },
     },
     customWidthGridStyle: {
         width: [12, 4, 4, 12, 12],
         css: css`
             @media print {
                 max-width: 100%;
                 flex-basis: 100%;
             }
         `,
     },
     fullWidthGridStyle: {
         width: 12,
         css: css`
             white-space: normal;
         `,
     },
     halfWidthGridStyle: {
         width: 6,
         css: css`
             padding-top: 15px;
             &:nth-child(odd) {
                 padding-left: 0;
             }
         `,
     },
     productImageGridStyle: {
         width: [12, 12, 12, 3, 3],
         css: css`
             max-width: 100px;
             flex-direction: column;

             ${screen("lte", "md")`
                  flex-direction: row;
                  &&& {
                      margin-bottom: 15px; 
                  }
              `}
         `,
     },
     productImageStyle: {
         css: css`
             border: 1px solid ${getColor("common.ui4")};
         `,
     },
     wrapper: {
         css: css`
             white-space: pre-wrap;
             margin-top: 15px;
             display: flex;
         `,
     },
     titleText: {
         size: 16,
         color: "text.main",
         weight: 700,
         css: css`
             display: block;
             letter-spacing: 0;
             padding-right: 5px;
         `,
     },
     contentText: {
         color: "primary.main",
         size: 16,
         weight: 500,
         css: css`
             white-space: pre-wrap;
             word-wrap: break-word;
             background: #ffffff;
         `,
     },
     tableCellStyle: {
         css: css`
             height: auto;
             vertical-align: top;
             border-bottom: 0;
             > div {
                 padding-bottom: 5px !important;
             }
             ${screen("lte", "md")`
                   display: flex;
                   align-items: center;
                  &.hide-on-mobile {
                      display:none;
                  }
             `}
             @media print {
                 &&& ${GridOffset} {
                     display: flex;
                     flex-direction: row;
                 }
             }
         `,
     },
     tableCellGridStyle: {
         width: [6, 6, 6, 12, 12],
         css: css`
             word-break: keep-all;
             white-space: break-spaces;
             font-size: 12px;
             &&& {
                 max-width: 100%;
                 padding: 0;
             }
             @media print {
                 max-width: 100%;
                 flex-basis: 100%;
             }
         `,
     },
     productPartNumbers: {
         container: { gap: 0 },
         erpNumberGridItem: {
             css: css`
                 padding: 0;
                 font-size: 14px;
                 line-height: 18px;
             `,
         },
         customerNameGridItem: {
             css: css`
                 padding: 0;
                 font-size: 14px;
                 line-height: 18px;
             `,
         },
         specNumberGridItem: {
             css: css`
                 padding: 0;
                 font-size: 14px;
                 line-height: 18px;
             `,
         },
         specNumberLabelText: {
             weight: "bold",
         },
         manufacturerItemGridItem: {
             css: css`
                 padding: 0;
                 font-size: 14px;
                 line-height: 18px;
             `,
         },
     },
 };

 type Props = OwnProps;
 const styles = orderDetailsOrderLineStyles;

 const CustomCartLineDetails: FC<Props> = ({ orderLine }) => {
     return (
         <>
             <DataTableRow key={orderLine.itemNumber} {...styles.tableRowStyle}>
                 <DataTableCell {...styles.tableCellStyle}>
                     <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                         <GridItem {...styles.productImageGridStyle} className="image-print">
                             <Clickable href={orderLine.productUrl} {...minicartStyles.cartLineImageStyles}>
                                 <LazyImage
                                     {...styles.productImageStyle}
                                     src={orderLine.imagePath}
                                     altText={orderLine.productName}
                                     height="82px"
                                     width="82px"
                                 />
                             </Clickable>
                         </GridItem>
                         <GridItem {...styles.productDetailsGridStyle} className="product-details-print">
                             <GridContainer gap={16}>
                                 {orderLine.productName && (
                                     <GridItem {...styles.fullWidthGridStyle}>
                                         <Clickable
                                             href={orderLine.productUrl}
                                             {...minicartStyles.cartLineDescriptionStyles}
                                         >
                                             <ProductDescription
                                                 product={orderLine}
                                                 extendedStyles={styles.productDescriptionStyles}
                                             />
                                         </Clickable>
                                     </GridItem>
                                 )}
                                 <GridItem {...styles.fullWidthGridStyle}>
                                     <ProductPartNumbers
                                         showLabel={true}
                                         showSpecNumber={true}
                                         showCustomerName={true}
                                         showManufacturerItem={true}
                                         showProductNumber={true}
                                         productNumber={orderLine.itemNumber}
                                         manufacturerItem={orderLine.manufacturerItem}
                                         specNumber={orderLine.specNumber || ""}
                                         extendedStyles={styles.productPartNumbers}
                                     />
                                 </GridItem>
                             </GridContainer>
                         </GridItem>
                     </GridContainer>
                 </DataTableCell>
                 <DataTableCell {...styles.tableCellStyle}>
                     <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                         <GridItem {...styles.tableCellGridStyle}>
                             {!!orderLine.qty && (
                                 <GridItem {...styles.customWidthGridStyle}>
                                     <Typography {...styles.contentText}>{orderLine.qty}</Typography>
                                 </GridItem>
                             )}
                         </GridItem>
                     </GridContainer>
                 </DataTableCell>
             </DataTableRow>
         </>
     );
 };

 export default CustomCartLineDetails;
 